<template>
  <div class="mb-24 mb-md-32">
    <v-progress-circular
      v-if="status === REQUEST_STATUS.LOADING"
      indeterminate
      color="primary"
      class="ma-6" />

    <v-container
      v-if="getOrder.attributes"
      fluid
      class="pa-5 pb-0">
      <div class="d-flex gap-12 font-sm-25">
        <p class="mb-0">
          Vytvořeno:
          <span class="g-u-whitespace-nowrap">{{ formatDate(getOrder.createdAt) }}</span>
        </p>
        <p class="mb-0">
          Změněno:
          <span class="g-u-whitespace-nowrap">{{ formatDate(getOrder.updatedAt) }}</span>
          <template v-if="getOrder.userEmail">
            - {{ getOrder.userEmail }}
          </template>
        </p>
      </div>

      <div class="d-flex align-center justify-center flex-wrap gap-4">
        <HeaderComponent
          :title="getOrder.externalId"
          class="text-center font-sm-25 font-md-30" />
        <h4>{{ getOrder.email }}</h4>

        <v-dialog
          v-model="noteDialog"
          scrollable
          maxWidth="1200px">
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="ml-2"
              v-bind="attrs"
              v-on="on">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="50"
                height="50"
                viewBox="0 0 640 512"
                :fill="form.note.length ? 'green' : ''">
                <path d="M514.7 23.11C539.6-2.142 580.4-2.267 605.5 22.83L617.4 34.75C642.4 59.74 642.4 100.3 617.4 125.3L424.9 317.8C416.1 326.6 404.9 332.7 392.8 335.2L307.3 353.2C301.1 354.3 296.5 352.6 292.7 348.8C288.9 344.1 287.2 339.5 288.3 334.2L306.3 248.6C308.8 236.5 314.8 225.5 323.4 216.7L514.7 23.11zM582.8 45.46C570.3 32.91 549.9 32.97 537.4 45.6L510.1 72.37L568 129.4L594.8 102.6C607.3 90.13 607.2 69.87 594.8 57.38L582.8 45.46zM337.6 255.1L324.7 316.8L386.2 303.9C392.3 302.6 397.9 299.6 402.3 295.2L545.4 152L488.5 95.14L346.2 239.2C341.9 243.6 338.9 249.1 337.6 255.1L337.6 255.1zM304 128C312.8 128 320 135.2 320 144C320 152.8 312.8 160 304 160H64C46.33 160 32 174.3 32 192V448C32 465.7 46.33 480 64 480H512C529.7 480 544 465.7 544 448V272C544 263.2 551.2 256 560 256C568.8 256 576 263.2 576 272V448C576 483.3 547.3 512 512 512H64C28.65 512 0 483.3 0 448V192C0 156.7 28.65 128 64 128H304zM104 320C104 306.7 114.7 296 128 296C141.3 296 152 306.7 152 320C152 333.3 141.3 344 128 344C114.7 344 104 333.3 104 320zM248 320C248 333.3 237.3 344 224 344C210.7 344 200 333.3 200 320C200 306.7 210.7 296 224 296C237.3 296 248 306.7 248 320z" />
              </svg>
            </button>
          </template>

          <v-card>
            <h4 class="text-uppercase pa-4">
              Zadejte poznámku k přiděleným kreditům
            </h4>
            <v-divider />
            <v-card-text class="pa-0 px-4">
              <v-text-field
                id="noteInput"
                v-model="form.note"
                label="Text k objednávce"
                outlined
                singleLine
                class="v-textarea--custom mt-4" />
            </v-card-text>

            <v-card-actions class="pa-0">
              <v-btn
                block
                tile
                large
                color="primary"
                class="btn-md-maxi"
                @click="noteDialog = false">
                {{ $vuetify.lang.t('$vuetify.ecoDetail.save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <div v-if="form.note">
        <h4 class="text-uppercase mb-3">
          {{ $vuetify.lang.t('$vuetify.ecoDetail.note') }}
        </h4>
        <p class="font-sm-20 font-md-30 mb-5">
          {{ form.note }}
        </p>
      </div>

      <p
        v-if="getOrder.status === 'GenesisCreditsAdded' && userLimitedCredits"
        class="text-red font-sm-20 font-md-30">
        {{ $vuetify.lang.t('$vuetify.ecoSearch.alreadyCleared') }}
      </p>
      <p
        v-if="getOrder.status === 'GenesisSendingToThirdPartiesApi' && userLimitedCredits"
        class="text-red font-sm-20 font-md-30">
        {{ $vuetify.lang.t('$vuetify.ecoSearch.errorStatus') }}
      </p>
    </v-container>
    <template v-if="(getOrder.status !== 'GenesisCreditsAdded' || !userLimitedCredits) && (getOrder.status !== 'GenesisSendingToThirdPartiesApi' || !userLimitedCredits)">
      <v-container
        v-if="getOrder.attributes"
        fluid
        class="px-5 py-0">
        <div class="d-flex justify-space-between flex-wrap-reverse gap-4 mb-3">
          <h4 class="text-uppercase">
            {{ $vuetify.lang.t('$vuetify.ecoDetail.records') }}
          </h4>
          <span class="font-sm-25">{{ formatStatus(getOrder.status) }}</span>
        </div>

        <section
          v-if="form.detail[0]"
          class="d-flex align-stretch gap-2 mb-md-6">
          <v-card
            class="flex-1">
            <v-container>
              <v-row class="align-center">
                <div class="col-auto mr-auto">
                  <v-card-text class="py-1 font-sm-40 font-md-50">
                    {{ form.detail[form.detail.length - 1].credits_quantity }} {{ $vuetify.lang.t('$vuetify.ecoDetail.credits') }}
                  </v-card-text>
                </div>
                <v-btn
                  v-if="!userOnlyPreview"
                  small
                  color="transparent"
                  class="col-auto action-button"
                  @click="deleteItem(form.detail.indexOf(form.detail[form.detail.length - 1]))">
                  <v-icon
                    normal
                    color="red"
                    class="extraBigIcon">
                    $vuetify.icons.delete
                  </v-icon>
                </v-btn>
              </v-row>
            </v-container>
          </v-card>

          <v-dialog
            v-model="dialog"
            scrollable
            maxWidth="900px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                height="auto"
                color="primary"
                class="btn-md-maxi"
                v-bind="attrs"
                v-on="on">
                {{ $vuetify.lang.t('$vuetify.ecoDetail.all') }}
              </v-btn>
            </template>

            <v-card>
              <h4 class="text-uppercase pa-4">
                {{ $vuetify.lang.t('$vuetify.ecoDetail.allCredits') }}
              </h4>
              <v-divider />
              <v-card-text class="pa-0">
                <div
                  v-for="(item, id) in form.detail"
                  :key="item.id">
                  <v-container>
                    <v-row class="align-center">
                      <div class="col-auto mr-auto">
                        <v-card-text class="pa-1 font-sm-40 font-md-50">
                          {{ item.credits_quantity }} {{ $vuetify.lang.t('$vuetify.ecoDetail.credits') }}
                        </v-card-text>
                      </div>
                      <v-btn
                        v-if="!userOnlyPreview"
                        small
                        color="transparent"
                        class="col-auto action-button"
                        @click="deleteItem(id)">
                        <v-icon
                          color="red"
                          class="extraBigIcon">
                          $vuetify.icons.delete
                        </v-icon>
                      </v-btn>
                    </v-row>
                  </v-container>
                  <v-divider />
                </div>
              </v-card-text>
              <v-card-actions class="pa-0">
                <v-btn
                  block
                  tile
                  large
                  color="primary"
                  class="btn-md-maxi"
                  @click="dialog = false">
                  {{ $vuetify.lang.t('$vuetify.ecoDetail.close') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </section>
        <p
          v-else
          class="text-red font-sm-20 font-md-30 ma-0 mb-4">
          {{ $vuetify.lang.t('$vuetify.ecoDetail.noCredits') }}
        </p>
      </v-container>

      <div
        v-if="getOrder.type === ECO_CHALLENGE_TYPE_ENUM.PACKETA || getOrder.type === ECO_CHALLENGE_TYPE_ENUM.CESKA_POSTA_BALIKOVNA"
        class="mt-4 mb-1 amber">
        <v-container fluid>
          <v-switch
            v-model="oversized"
            dense
            hideDetails
            :disabled="userOnlyPreview"
            class="oversized-input mt-0 font-weight-bold"
            :label="oversized ? $vuetify.lang.t('$vuetify.ecoDetail.oversized') : $vuetify.lang.t('$vuetify.ecoDetail.noOversized')" />
        </v-container>
      </div>

      <section class="d-flex gap-1 text-white">
        <div class="flex-1 success font-big-info">
          <strong>{{ $vuetify.lang.t('$vuetify.ecoDetail.pieces') }}:</strong> {{ form.detail.length }}
        </div>
        <div class="flex-1 success font-big-info">
          <strong>{{ $vuetify.lang.t('$vuetify.ecoDetail.credits') }}:</strong> {{ form.detail.reduce((sum, item) => sum + item.credits_quantity, 0) }}
        </div>
      </section>

      <v-container
        v-if="!userOnlyPreview"
        fluid
        class="pa-5">
        <p
          v-if="isAdmin === false"
          class="text-red font-sm-25 font-md-30">
          {{ $vuetify.lang.t('$vuetify.ecoDetail.noRights') }}
        </p>
        <h4 class="d-sm-none text-uppercase mb-3">
          {{ $vuetify.lang.t('$vuetify.ecoDetail.creditsTitle') }}
        </h4>
        <form @submit.prevent="onSubmit">
          <div class="d-flex justify-stretch gap-1 mb-4 mb-md-8">
            <v-text-field
              id="creditsInput"
              v-model="credits"
              :label="$vuetify.lang.t('$vuetify.ecoDetail.creditsTitle')"
              type="number"
              min="0"
              max="10000"
              outlined
              singleLine
              hideDetails
              prependInnerIcon="$vuetify.icons.plus"
              class="v-input--custom"
              :disabled="is_screen_tablet" />
            <v-btn
              height="auto"
              color="success"
              type="submit"
              class="d-sm-none"
              :disabled="credits<=0">
              <v-icon>$vuetify.icons.check</v-icon>
            </v-btn>
          </div>
          <p
            v-if="isTooBig === true"
            class="text-red mb-4 mb-md-8 font-sm-25 font-md-30">
            {{ $vuetify.lang.t('$vuetify.ecoDetail.isTooBig') }}
          </p>
          <div class="fast-add gap-2 w-100">
            <v-btn
              color="primary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(0)">
              <span class="pr-16">+0</span>

              <CreditsMultiplication
                :type="0"
                @callParentFunction="fastMultiplicationAdd(0)" />
            </v-btn>
            <v-btn
              color="primary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(1)">
              <span class="pr-16">+1</span>

              <CreditsMultiplication
                :type="1"
                @callParentFunction="fastMultiplicationAdd(1)" />
            </v-btn>
            <v-btn
              color="primary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(50)">
              <span class="pr-16">+50</span>

              <CreditsMultiplication
                :type="50"
                @callParentFunction="fastMultiplicationAdd(50)" />
            </v-btn>
            <v-btn
              color="primary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(100)">
              <span class="pr-16">+100</span>

              <CreditsMultiplication
                :type="100"
                @callParentFunction="fastMultiplicationAdd(100)" />
            </v-btn>
            <v-btn
              color="primary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(120)">
              <span class="pr-16">+120</span>

              <CreditsMultiplication
                :type="120"
                @callParentFunction="fastMultiplicationAdd(120)" />
            </v-btn>
            <v-btn
              color="primary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(150)">
              <span class="pr-16">+150</span>

              <CreditsMultiplication
                :type="150"
                @callParentFunction="fastMultiplicationAdd(150)" />
            </v-btn>

            <v-btn
              color="secondary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(379)">
              <span class="pr-16">+379</span>

              <CreditsMultiplication
                :type="379"
                @callParentFunction="fastMultiplicationAdd(379)" />
            </v-btn>
            <v-btn
              color="secondary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(529)">
              <span class="pr-16">+529</span>

              <CreditsMultiplication
                :type="529"
                @callParentFunction="fastMultiplicationAdd(529)" />
            </v-btn>
            <v-btn
              color="secondary"
              class="v-btn--superLarge"
              :disabled="clicked"
              @click="fastAdd(729)">
              <span class="pr-16">+729</span>

              <CreditsMultiplication
                :type="729"
                @callParentFunction="fastMultiplicationAdd(729)" />
            </v-btn>

            <v-btn
              color="black"
              class="fast-add__full-row d-sm-none v-btn--superLarge"
              @click="focusInput()">
              <span>+JINÉ</span>
            </v-btn>
          </div>

          <v-divider class="d-none d-sm-flex my-3 my-md-5" />

          <section class="numberpad mt-4">
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(7)">
              <span>7</span>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(8)">
              <span>8</span>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(9)">
              <span>9</span>
            </v-btn>

            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(4)">
              <span>4</span>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(5)">
              <span>5</span>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(6)">
              <span>6</span>
            </v-btn>

            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(1)">
              <span>1</span>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(2)">
              <span>2</span>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(3)">
              <span>3</span>
            </v-btn>

            <v-btn
              color="black"
              class="v-btn--superLarge extraBigIcon"
              @click="del">
              <v-icon>$vuetify.icons.backspace</v-icon>
            </v-btn>
            <v-btn
              color="black"
              class="v-btn--superLarge"
              @click="append(0)">
              <span>0</span>
            </v-btn>
            <v-btn
              color="success"
              type="submit"
              class="v-btn--superLarge extraBigIcon"
              :disabled="credits===0">
              <v-icon>$vuetify.icons.check</v-icon>
            </v-btn>
          </section>
        </form>
      </v-container>
    </template>

    <FooterComponent>
      <template v-slot:actions>
        <v-btn
          v-if="(!form.detail[0] || (getOrder.status === 'GenesisCreditsAdded' && userLimitedCredits) || getOrder.status === 'GenesisSendingToThirdPartiesApi' && userLimitedCredits) || userOnlyPreview"
          color="grey lighten-1"
          class="mr-5 btn-md-maxi"
          :to="{ name: 'processing' }">
          {{ $vuetify.lang.t('$vuetify.close') }}
        </v-btn>

        <v-dialog
          v-else
          v-model="closeDialog"
          maxwidth="900">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="grey lighten-1"
              class="mr-5 btn-md-maxi"
              v-bind="attrs"
              v-on="on">
              {{ $vuetify.lang.t('$vuetify.close') }}
            </v-btn>
          </template>

          <v-card>
            <h4 class="text-uppercase px-5 py-3">
              {{ $vuetify.lang.t('$vuetify.ecoDetail.closePopup') }}
            </h4>

            <v-divider />

            <v-card-actions class="d-flex justify-space-between py-3">
              <v-btn
                color="grey lighten-1"
                class="mr-5 btn-md-maxi"
                @click="closeDialog = false">
                {{ $vuetify.lang.t('$vuetify.ecoDetail.popupBack') }}
              </v-btn>
              <v-btn
                color="primary"
                class="btn-md-maxi"
                :to="{ name: 'processing' }"
                @click="closeDialog = false">
                {{ $vuetify.lang.t('$vuetify.ecoDetail.popupAgree') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-btn
          v-if="!userOnlyPreview"
          color="primary"
          class="btn-md-maxi"
          :disabled="!form.detail[0] || (getOrder.status === 'GenesisCreditsAdded' && userLimitedCredits) || getOrder.status === 'GenesisSendingToThirdPartiesApi' && userLimitedCredits || status === REQUEST_STATUS.LOADING"
          @click="sendCredits()">
          <span v-if="status !== REQUEST_STATUS.LOADING">{{ $vuetify.lang.t('$vuetify.basic.save') }}</span>

          <v-progress-circular
            v-if="status === REQUEST_STATUS.LOADING"
            indeterminate
            color="white" />
        </v-btn>
      </template>
    </FooterComponent>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import FooterComponent from '../../components/layout/FooterComponent.vue';
import CreditsMultiplication from '../../components/detail/creditsMultiplication.vue';

import {formatDateTime} from "@/common/helpers/date";
import {formatStatusToReadable} from "@/common/helpers/status";

import REQUEST_STATUS from '../../common/enums/requestStatus';
import ECO_CHALLENGE_TYPE_ENUM from "@/common/enums/ecoChallengeTypes";

export default {
  name: 'EcoDetail',

  components: {
    HeaderComponent,
    FooterComponent,
    CreditsMultiplication,
  },

  data() {
    return {
      REQUEST_STATUS,
      ECO_CHALLENGE_TYPE_ENUM,
      credits: null,
      clicked: false,
      closeDialog: false,
      dialog: false,
      noteDialog: false,
      isAdmin: true,
      isTooBig: false,
      oversized: false,
      form: {
        "status": "GenesisCreditsAdded",
        "note": "",
        "user_id": "",
        "detail": [],
        "shipping": 0,
        "oversized_shipping_fine": 0,
        "oversized_shipping": false,
      },
    }
  },

  computed: {
    ...mapGetters({
      status: 'orders/getStatus',
      getOrder: 'orders/getOrder',
      getUser: 'auth/getUser',
      userRoles: 'auth/getUserRoles',
    }),

    is_screen_tablet() {
      return this.$vuetify.breakpoint.smAndUp
    },

    orderId() {
      return Number(this.$route.params.id);
    },

    userLimitedCredits() {
      return this.userRoles.some(r => r.id === parseInt(process.env.VUE_APP_LIMITED_CREDIT_ADDITION_USER_ROLE_ID));
    },

    userOnlyPreview() {
      return this.userRoles.some(r => r.id === parseInt(process.env.VUE_APP_ONLY_PREVIEW_USER_ROLE_ID));
    },
  },

  created() {
    this.fetchOrder(this.orderId).then(() => {
      if (this.getOrder.ecoCreditsDetail) {
        this.getOrder.ecoCreditsDetail.forEach(item => {
          this.form.detail.push({
            credits_quantity: item.credits_quantity_without_coefficient,
            products_quantity:  item.products_quantity,
            note: item.note,
          })
        });
      }

      if (this.getOrder.ecoCredits) {
        this.oversized = this.getOrder.ecoCredits.oversized_shipping
      }

      if (this.getOrder.note) {
        this.form.note = this.getOrder.note
      }
    })
  },

  methods: {
    ...mapActions({
      fetchOrder: 'orders/fetchOrder',
      updateCredits: 'orders/updateCredits',
    }),

    formatDate(date) {
      return formatDateTime(date);
    },

    formatStatus(status) {
      return formatStatusToReadable(status);
    },

    append(number) {
      this.credits = Number('' + (this.credits == null ? "" : this.credits) + number);
    },

    del() {
      this.credits = this.credits / 10 ^ 0;
    },

    focusInput() {
      document.getElementById("creditsInput").focus()
    },

    fastAdd(number) {
      if (this.clicked) {
        return;
      }

      this.clicked = true;

      this.form.detail.push({ credits_quantity: number, products_quantity: 1, note: null });

      setTimeout(function(){
        this.clicked = false;
      }.bind(this),300);
    },

    fastMultiplicationAdd(number) {
      this.form.detail.push({ credits_quantity: number, products_quantity: 1, note: null });
    },

    onSubmit() {
      if(Number(this.credits) <= 10000){
        this.isTooBig = false;

        this.form.detail.push({ credits_quantity: Number(this.credits), products_quantity: 1, note: null });
        this.credits = null;
      } else {
        this.isTooBig = true;
      }
    },

    deleteItem(id) {
      this.form.detail.splice(id, 1);
    },

    sendCredits() {
      if((this.userLimitedCredits === true) && (this.form.detail.reduce((sum, item) => sum + item.credits_quantity, 0) > 10000)) {
        this.isAdmin = false
        return
      }

      if (this.form.detail.some(e => e.credits_quantity <= 50)) {
        // Get the count of records that meet the criteria
        const newCount = this.form.detail.filter(e => e.credits_quantity <= 50).length;

        // Create a regular expression to find the number in parentheses
        const regex = /\(\d+x\)/;

        // Replace the number in parentheses with the new count
        this.form.note = this.form.note.replace(regex, `(${newCount}x)`);

        // Add the text "Nesplňuje kritéria Eko-výzvy (new count)" only if it does not yet exist in this.form.note
        const searchText = "Nesplňuje kritéria Eko-výzvy";
        if (!this.form.note.includes(searchText)) {
          if(this.form.note.length) {
            this.form.note += ", "
          }

          this.form.note += `${searchText} (${newCount}x)`;
        }
      }

      if (this.oversized && (this.getOrder.type === this.ECO_CHALLENGE_TYPE_ENUM.PACKETA || this.getOrder.type === this.ECO_CHALLENGE_TYPE_ENUM.CESKA_POSTA_BALIKOVNA)) {
        this.form.oversized_shipping_fine = 2500
        this.form.oversized_shipping = true
      } else {
        this.form.oversized_shipping_fine = 0
        this.form.oversized_shipping = false
      }

      if (this.getOrder.type === this.ECO_CHALLENGE_TYPE_ENUM.PACKETA) {
        this.form.shipping = 0
      } else if (this.getOrder.type === this.ECO_CHALLENGE_TYPE_ENUM.CESKA_POSTA_BALIKOVNA) {
        this.form.shipping = 390
      }

      this.form.user_id = this.getUser.attributes.attributes.id;

      this.updateCredits({ url: ((this.getOrder.status === 'GenesisCreditsAdded' ? "update-credits/" : "add-credits/") + this.getOrder.externalId), formData: this.form }).then(() => {
        if (this.status === "success") {
          this.$router.push({ name: 'processing' });
        }
      });
    },
  },
}
</script>

<style lang="scss">
.font-big-info {
  padding: 0.5rem 1.25rem;
}

@media screen and (min-width: 600px) {
  h4 {
    font-size: 1.5rem;
  }

  .font-sm-20 {
    font-size: 1rem;
  }

  .font-sm-25 {
    font-size: 1.5rem;
  }

  .font-sm-40 {
    font-size: 2rem;
    line-height: initial;
  }

  .font-sm-50 {
    font-size: 3rem;
    line-height: initial;
  }

  .font-big-info {
    font-size: 2rem;
    line-height: initial;

    padding: 0.875rem 1.5rem;
  }

  .extraBigIcon svg {
    height: 2rem !important;
    width: 2rem !important;
  }

  .btn-md-maxi {
    padding: 1rem 3rem !important;

    height: auto !important;

    font-size: 2rem !important;
  }
}


@media screen and (min-width: 960px) {
  h4 {
    font-size: 1.875rem;
  }

  .font-md-30 {
    font-size: 1.875rem;
  }

  .font-md-50 {
    font-size: 3rem;
    line-height: initial;
  }

  .font-big-info {
    line-height: initial;

    padding: 1.5rem;
  }

  .extraBigIcon svg {
    height: 3rem !important;
    width: 3rem !important;
  }

  .btn-md-maxi {
    padding: 1.5rem 3.25rem !important;

    height: auto !important;

    font-size: 2rem !important;
  }

  .oversized-input {
    zoom: 1.5;
  }
}

.numberpad {
  display: none;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 0.875rem;

  @media screen and (min-width: 600px) {
    display: grid;
  }

  @media screen and (min-width: 960px) {
    display: grid;

    grid-gap: 1.5rem;
  }
}

.fast-add {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 0.875rem;

  @media screen and (min-width: 600px) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (min-width: 960px) {
    grid-gap: 1.5rem;
  }
}

.fast-add__full-row {
  grid-column: 1 / 4;
}

.v-input--custom {
  @media screen and (min-width: 600px) {
    zoom: 1.5;
  }

  @media screen and (min-width: 960px) {
    zoom: 2.25;
  }

  .v-text-field__slot{
    border-left: 1px solid lightgrey;
    margin-left: 0.375rem;

    label {
      padding: 0 0.5rem;
    }

    input {
      padding-left: 0.5rem;

      max-height: none;

      @media screen and (min-width: 960px) {
        font-size: 1.75rem;
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
      }

      &:disabled {
        color: rgba(0, 0, 0, 0.87);
      }
    }
  }
}

.v-textarea--custom {
  @media screen and (min-width: 600px) {
    zoom: 1.5;
  }

  @media screen and (min-width: 960px) {
    zoom: 2.5;
  }
}

.v-btn {
  &.v-btn--superLarge {
    height: 5rem !important;

    font-size: 2rem;

    @media screen and (min-width: 960px) {
      height: 7.5rem !important;

      font-size: 3rem;
    }
  }

  &.v-btn--multiplication {
    position: absolute;
    right: -1rem;
    top: 0;

    height: 100% !important;

    background: transparent !important;
    box-shadow: none;
    border-left: 1px solid white;
    border-radius: 0;

    color: white;

    svg {
      @media screen and (min-width: 960px) {
        height: 3rem;
        width: 3rem;

        margin: 0 0.5rem;
      }
    }
  }

  &.black {
    color: #ffffff;
  }
}

</style>
